/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { Routes, Route, useNavigate, Navigate, Link, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Trophy, Wallet, Calendar, Bell, Loader2, LogOut, Home, User, CreditCard, ListChecks,
  Banknote, Trophy as TrophyIcon, CalendarDays, Crown, MessageCircle, Lock, Mail, ArrowRight, Menu, X,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const tokenKey = "brivo_club_token";
const memberKey = "brivo_club_member";
const clubKey = "brivo_club_meta";

function setSession(tok, member, club) {
  localStorage.setItem(tokenKey, tok);
  localStorage.setItem(memberKey, JSON.stringify(member));
  localStorage.setItem(clubKey, JSON.stringify(club));
}
function getMember() { try { return JSON.parse(localStorage.getItem(memberKey) || "null"); } catch { return null; } }
function getClub() { try { return JSON.parse(localStorage.getItem(clubKey) || "null"); } catch { return null; } }
function getToken() { return localStorage.getItem(tokenKey) || ""; }
function clearSession() { [tokenKey, memberKey, clubKey].forEach((k) => localStorage.removeItem(k)); }
function capi() { return axios.create({ baseURL: API, headers: { Authorization: `Bearer ${getToken()}` } }); }

const ngn = (n) => `₦${Number(n || 0).toLocaleString()}`;

export default function ClubApp() {
  const member = getMember();
  // PWA: dynamically inject manifest + theme color when on /club/*
  useEffect(() => {
    let manifestLink = document.querySelector('link[rel="manifest"]#brivo-club-manifest');
    if (!manifestLink) {
      manifestLink = document.createElement("link");
      manifestLink.rel = "manifest";
      manifestLink.id = "brivo-club-manifest";
      manifestLink.href = "/club-manifest.json";
      document.head.appendChild(manifestLink);
    }
    let themeMeta = document.querySelector('meta[name="theme-color"]#brivo-club-theme');
    if (!themeMeta) {
      themeMeta = document.createElement("meta");
      themeMeta.name = "theme-color";
      themeMeta.id = "brivo-club-theme";
      themeMeta.content = "#10B981";
      document.head.appendChild(themeMeta);
    }
    return () => {
      manifestLink?.remove();
      themeMeta?.remove();
    };
  }, []);
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/app/*" element={member ? <Shell /> : <Navigate to="/club/login" replace />} />
    </Routes>
  );
}

function Landing() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-900 via-emerald-800 to-green-900 text-white flex flex-col">
      <div className="max-w-5xl mx-auto px-6 py-16 flex-1 flex flex-col items-center justify-center text-center">
        <div className="w-20 h-20 rounded-3xl bg-white/15 backdrop-blur border border-white/20 flex items-center justify-center mb-6"><Trophy size={36} /></div>
        <div className="text-xs uppercase font-bold tracking-[0.3em] text-white/70">Member's Portal</div>
        <h1 className="font-display font-extrabold text-5xl lg:text-6xl mt-3 max-w-3xl">Your club, in your pocket.</h1>
        <p className="text-base text-white/75 mt-4 max-w-xl">Pay dues, RSVP events, join the draft competition — everything your football club needs in one beautiful app.</p>
        <Link to="/club/login" data-testid="club-landing-login"
          className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-white text-emerald-900 font-bold">
          Member sign in <ArrowRight size={14} />
        </Link>
        <div className="mt-12 text-xs text-white/55">Not a member? Ask your club admin to add you.</div>
      </div>
    </div>
  );
}

function Login() {
  const nav = useNavigate();
  const [f, setF] = useState({ tenant_subdomain: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.tenant_subdomain || !f.email || !f.password) return toast.error("All fields required");
    setBusy(true);
    try {
      const r = await axios.post(`${API}/club/auth/login`, f);
      setSession(r.data.token, r.data.member, r.data.club);
      toast.success(`Welcome back, ${r.data.member.full_name}`);
      nav("/club/app/dashboard");
    } catch (e) { toast.error(e?.response?.data?.detail || "Login failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-emerald-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl border border-black/8 p-8 max-w-md w-full">
        <div className="text-center mb-6">
          <div className="w-14 h-14 rounded-2xl bg-emerald-600 text-white flex items-center justify-center mx-auto mb-3"><Trophy size={26} /></div>
          <h1 className="font-display font-extrabold text-2xl text-black">Member sign in</h1>
          <p className="text-sm text-black/55 mt-1">Welcome back to your club portal.</p>
        </div>
        <div className="space-y-3">
          <Input icon={Trophy} placeholder="Club code (e.g. acme)" value={f.tenant_subdomain} onChange={(v) => setF({ ...f, tenant_subdomain: v })} testid="club-login-subdomain" />
          <Input icon={Mail} type="email" placeholder="Email" value={f.email} onChange={(v) => setF({ ...f, email: v })} testid="club-login-email" />
          <Input icon={Lock} type="password" placeholder="Password" value={f.password} onChange={(v) => setF({ ...f, password: v })} testid="club-login-password" />
          <button onClick={submit} disabled={busy} data-testid="club-login-submit"
            className="w-full py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold disabled:opacity-50">
            {busy ? "Signing in…" : "Sign in"}
          </button>
        </div>
        <div className="mt-6 text-center text-xs text-black/55">Forgot password? Contact your club administrator.</div>
      </div>
    </div>
  );
}

function Input({ icon: Icon, testid, value, onChange, ...rest }) {
  return (
    <div className="relative">
      {Icon && <Icon size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-black/40" />}
      <input {...rest} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className={`w-full ${Icon ? "pl-10" : "pl-3"} pr-3 py-3 rounded-xl border border-black/15 text-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20`} />
    </div>
  );
}

// ───────── App Shell ─────────
function Shell() {
  const member = getMember();
  const club = getClub();
  const nav = useNavigate();
  const loc = useLocation();
  const [drawer, setDrawer] = useState(false);
  const logout = () => { clearSession(); nav("/club"); };

  const MENU = [
    { k: "dashboard", lbl: "Dashboard", icon: Home, to: "/club/app/dashboard" },
    { k: "profile", lbl: "My Profile", icon: User, to: "/club/app/profile" },
    { k: "payments", lbl: "My Payments", icon: CreditCard, to: "/club/app/payments" },
    { k: "dues", lbl: "My Dues", icon: ListChecks, to: "/club/app/dues" },
    { k: "loans", lbl: "Loan Requests", icon: Banknote, to: "/club/app/loans" },
    { k: "draft", lbl: "Draft Competition", icon: TrophyIcon, to: "/club/app/draft" },
    { k: "matches", lbl: "Match Schedule", icon: Calendar, to: "/club/app/matches" },
    { k: "events", lbl: "Events", icon: CalendarDays, to: "/club/app/events" },
    { k: "executives", lbl: "Executives", icon: Crown, to: "/club/app/executives" },
    { k: "notifications", lbl: "Notifications", icon: Bell, to: "/club/app/notifications" },
    { k: "support", lbl: "Support", icon: MessageCircle, to: "/club/app/support" },
  ];

  return (
    <div className="min-h-screen bg-[#F5F8F4] flex">
      {/* Drawer (mobile) */}
      {drawer && <div className="lg:hidden fixed inset-0 bg-black/40 z-40" onClick={() => setDrawer(false)} />}
      <aside className={`fixed lg:sticky top-0 left-0 h-screen w-72 bg-white border-r border-black/8 z-50 transition-transform ${drawer ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="h-16 px-5 border-b border-black/8 flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-emerald-600 text-white flex items-center justify-center"><Trophy size={17} /></div>
          <div className="font-display font-extrabold text-base truncate">{club?.name || "Club"}</div>
          <button className="ml-auto lg:hidden" onClick={() => setDrawer(false)}><X size={16} /></button>
        </div>
        <div className="p-4 border-b border-black/8 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-green-700 text-white flex items-center justify-center font-display font-extrabold overflow-hidden">
            {member?.photo_url ? <img src={member.photo_url} alt="" className="w-full h-full object-cover" /> : (member?.full_name || "M")[0]}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold truncate">{member?.full_name}</div>
            <div className="flex items-center gap-1 mt-0.5">
              <div className="text-[10px] font-mono bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded">{member?.membership_no}</div>
              {member?.playing_position && (
                <div className="text-[10px] font-bold bg-black text-white px-1.5 py-0.5 rounded uppercase tracking-wider">{member.playing_position}</div>
              )}
            </div>
          </div>
        </div>
        <nav className="p-3 space-y-1 overflow-y-auto" style={{ maxHeight: "calc(100vh - 220px)" }}>
          {MENU.map((m) => {
            const Icon = m.icon, active = loc.pathname === m.to;
            return (
              <Link key={m.k} to={m.to} data-testid={`club-nav-${m.k}`}
                onClick={() => setDrawer(false)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold ${active ? "bg-emerald-600 text-white" : "text-black/65 hover:bg-emerald-50 hover:text-emerald-700"}`}>
                <Icon size={15} /> {m.lbl}
              </Link>
            );
          })}
        </nav>
        <div className="absolute bottom-0 inset-x-0 p-3 border-t border-black/8 bg-white">
          <button onClick={logout} data-testid="club-logout"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold text-red-600 hover:bg-red-50">
            <LogOut size={15} /> Sign out
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        <header className="lg:hidden bg-white border-b border-black/8 h-14 flex items-center px-4 gap-3">
          <button onClick={() => setDrawer(true)} data-testid="club-drawer-toggle"><Menu size={18} /></button>
          <div className="font-display font-extrabold text-base truncate">{club?.name}</div>
        </header>
        <Routes>
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="profile" element={<Profile />} />
          <Route path="payments" element={<Payments />} />
          <Route path="dues" element={<Dues />} />
          <Route path="loans" element={<MemberLoans />} />
          <Route path="draft" element={<MemberDraft />} />
          <Route path="matches" element={<MemberMatches />} />
          <Route path="events" element={<MemberEvents />} />
          <Route path="executives" element={<MemberExecs />} />
          <Route path="notifications" element={<MemberNotifs />} />
          <Route path="support" element={<ComingSoon title="Support" subtitle="Talk to your club administrator." />} />
          <Route path="*" element={<Navigate to="/club/app/dashboard" replace />} />
        </Routes>
      </div>
    </div>
  );
}

function Dashboard() {
  const [d, setD] = useState(null);
  const [settings, setSettings] = useState(null);
  useEffect(() => {
    capi().get("/club/dashboard").then((r) => setD(r.data)).catch((e) => toast.error(e?.response?.data?.detail || "Failed"));
    capi().get("/club/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);
  if (!d) return <div className="p-12 flex items-center justify-center"><Loader2 className="animate-spin text-emerald-600" /></div>;
  const k = d.kpis;
  const freq = settings?.dues_frequency || "monthly";
  const freqLabel = { weekly: "weekly", monthly: "monthly", yearly: "yearly", instant: "one-time" }[freq] || freq;
  return (
    <div className="p-5 lg:p-8" data-testid="club-dashboard">
      <h1 className="font-display font-extrabold text-2xl lg:text-3xl text-black">Welcome, {d.member.full_name.split(" ")[0]}</h1>
      <p className="text-sm text-black/55 mt-1">Here's your club at a glance.</p>

      {settings && (settings.monthly_dues > 0 || settings.registration_fee > 0) && (
        <div className="mt-5 rounded-2xl p-5 bg-gradient-to-br from-emerald-600 to-green-800 text-white" data-testid="club-fees-banner">
          <div className="text-[10px] uppercase font-bold tracking-[0.2em] text-white/75">Your club fees</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
            <FeeChip label="Registration" amount={settings.registration_fee} sub="one-off" />
            <FeeChip label="Dues" amount={settings.monthly_dues} sub={freqLabel} />
            <FeeChip label="Match levy" amount={settings.match_levy} sub="per match" />
            <FeeChip label="Donation" amount={settings.donation_suggested} sub="suggested" />
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-5">
        <Card title="Membership" value={k.membership_status} subtitle="Status" accent={k.membership_status === "active" ? "from-emerald-500 to-green-600" : "from-amber-500 to-orange-500"} />
        <Card title="Dues" value={ngn(k.outstanding_dues)} subtitle={`${k.pending_dues_count} pending`} accent="from-rose-500 to-red-600" />
        <Card title="Events" value={k.upcoming_events} subtitle="Upcoming" accent="from-violet-500 to-fuchsia-500" />
        <Card title="Loans" value={k.loans_active} subtitle="Active" accent="from-blue-500 to-indigo-500" />
      </div>

      <div className="mt-6 bg-white border border-black/8 rounded-2xl overflow-hidden">
        <div className="px-5 py-3 border-b border-black/5 flex items-center justify-between">
          <div className="font-display font-extrabold text-base">Pending dues</div>
          <Link to="/club/app/dues" className="text-xs font-bold text-emerald-700 hover:underline">View all</Link>
        </div>
        {d.pending_dues.length === 0 ? <div className="p-6 text-sm text-black/45 text-center">All clear — no pending dues.</div> : (
          <div className="divide-y divide-black/5">
            {d.pending_dues.slice(0, 5).map((due) => (
              <div key={due.id} className="px-5 py-3 flex items-center justify-between">
                <div>
                  <div className="font-bold text-sm">{due.title}</div>
                  <div className="text-xs text-black/55 capitalize">{(due.kind || "").replace("_", " ")}</div>
                </div>
                <div className="text-right">
                  <div className="font-display font-extrabold text-base text-red-600">{ngn(due.amount)}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-5 bg-white border border-black/8 rounded-2xl overflow-hidden">
        <div className="px-5 py-3 border-b border-black/5 font-display font-extrabold text-base">Recent payments</div>
        {d.recent_payments.length === 0 ? <div className="p-6 text-sm text-black/45 text-center">No payments yet.</div> : (
          <div className="divide-y divide-black/5">
            {d.recent_payments.map((p) => (
              <div key={p.id} className="px-5 py-3 flex items-center justify-between">
                <div>
                  <div className="font-bold text-sm capitalize">{(p.kind || "").replace("_", " ")}</div>
                  <div className="text-xs text-black/55">{(p.paid_at || p.created_at || "").slice(0, 10)}</div>
                </div>
                <div className="text-right">
                  <div className="font-display font-extrabold text-base text-emerald-700">{ngn(p.amount)}</div>
                  <PaymentStatusBadge status={p.status} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function FeeChip({ label, amount, sub }) {
  return (
    <div className="bg-white/15 backdrop-blur border border-white/20 rounded-xl p-3">
      <div className="text-[10px] uppercase font-bold tracking-wider text-white/75">{label}</div>
      <div className="font-display font-extrabold text-lg mt-0.5">{amount > 0 ? ngn(amount) : "—"}</div>
      <div className="text-[10px] text-white/65 capitalize">{sub}</div>
    </div>
  );
}

function PaymentStatusBadge({ status }) {
  const s = status || "approved";
  const cls = s === "pending_approval" ? "bg-amber-100 text-amber-700" :
              s === "rejected" ? "bg-rose-100 text-rose-700" :
              "bg-emerald-100 text-emerald-700";
  const lbl = s === "pending_approval" ? "Pending review" : s === "rejected" ? "Rejected" : "Approved";
  return <span className={`inline-block mt-0.5 px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide ${cls}`}>{lbl}</span>;
}

function Profile() {
  const member = getMember();
  const [stats, setStats] = useState(null);
  useEffect(() => {
    capi().get("/club/me/stats").then((r) => setStats(r.data)).catch(() => setStats({ totals: {}, team: null, fixtures: [] }));
  }, []);
  const formInfo = {
    Excellent: { emoji: "🔥", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    Good: { emoji: "✅", color: "bg-blue-50 text-blue-700 border-blue-200" },
    Fair: { emoji: "➖", color: "bg-amber-50 text-amber-700 border-amber-200" },
    Poor: { emoji: "⚠️", color: "bg-rose-50 text-rose-700 border-rose-200" },
    Recovering: { emoji: "🩹", color: "bg-violet-50 text-violet-700 border-violet-200" },
  };
  const fi = formInfo[member?.form] || null;
  const totals = stats?.totals || { goals: 0, assists: 0, matches_played: 0, yellow_cards: 0, red_cards: 0 };
  return (
    <div className="p-5 lg:p-8 max-w-3xl" data-testid="club-profile">
      <h1 className="font-display font-extrabold text-2xl text-black mb-5">My profile</h1>
      <div className="bg-white border border-black/8 rounded-2xl p-6">
        <div className="flex items-center gap-4 mb-5">
          <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-emerald-500 to-green-700 text-white flex items-center justify-center overflow-hidden flex-shrink-0">
            {member?.photo_url ? (
              <img src={member.photo_url} alt={member.full_name} className="w-full h-full object-cover" data-testid="club-profile-photo" />
            ) : (
              <span className="font-display font-extrabold text-3xl">{(member?.full_name || "M")[0]}</span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-extrabold text-xl truncate">{member?.full_name}</div>
            <code className="text-xs font-mono bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded">{member?.membership_no}</code>
            <div className="mt-2 flex items-center gap-1.5 flex-wrap">
              {stats?.team && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-white text-[10px] font-bold uppercase tracking-wider" style={{ background: stats.team.team_color || "#1FA868" }}>
                  ⚽ {stats.team.team_name}
                </span>
              )}
              {member?.playing_position && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-black text-white text-[10px] font-bold uppercase tracking-wider" data-testid="club-profile-position">
                  {member.playing_position}
                </span>
              )}
              {member?.jersey_no != null && member.jersey_no !== "" && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-600 text-white text-[10px] font-bold uppercase tracking-wider">
                  #{member.jersey_no}
                </span>
              )}
              {fi && (
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md border text-[10px] font-bold uppercase tracking-wider ${fi.color}`} data-testid="club-profile-form">
                  {fi.emoji} {member.form}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Career stats */}
        <div className="mb-6" data-testid="club-stats-card">
          <div className="text-[10px] uppercase font-bold tracking-wider text-black/55 mb-2">Career stats</div>
          <div className="grid grid-cols-5 gap-2">
            <StatBox emoji="⚽" v={totals.goals || 0} lbl="Goals" testid="club-stat-goals" />
            <StatBox emoji="🎯" v={totals.assists || 0} lbl="Assists" testid="club-stat-assists" />
            <StatBox emoji="👟" v={totals.matches_played || 0} lbl="Matches" testid="club-stat-matches" />
            <StatBox emoji="🟨" v={totals.yellow_cards || 0} lbl="Yellow" testid="club-stat-yellow" />
            <StatBox emoji="🟥" v={totals.red_cards || 0} lbl="Red" testid="club-stat-red" />
          </div>
        </div>

        <Detail label="Email" value={member?.email} />
        <Detail label="Phone" value={member?.phone} />
        <Detail label="Gender" value={member?.gender} />
        <Detail label="Date of Birth" value={member?.date_of_birth} />
        <Detail label="State / LGA" value={`${member?.state || "—"} / ${member?.lga || "—"}`} />
        <Detail label="Occupation" value={member?.occupation} />
        <Detail label="Address" value={member?.address} />
        <Detail label="Emergency contact" value={member?.emergency_contact} />
      </div>

      {/* Recent match contributions */}
      {stats && stats.fixtures.length > 0 && (
        <div className="mt-5 bg-white border border-black/8 rounded-2xl overflow-hidden" data-testid="club-stats-fixtures">
          <div className="px-5 py-3 border-b border-black/5 font-display font-extrabold text-base">My match contributions</div>
          <div className="divide-y divide-black/5">
            {stats.fixtures.map((f) => (
              <div key={f.fixture_id} className="px-5 py-3 flex items-center justify-between flex-wrap gap-2">
                <div className="min-w-0">
                  <div className="font-bold text-sm">{f.team_a_name} {f.team_a_score} – {f.team_b_score} {f.team_b_name}</div>
                  <div className="text-[10px] text-black/55">{(f.scheduled_at || "").slice(0, 10)}{f.is_mvp && <span className="ml-2 px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-700 font-bold uppercase tracking-wider">⭐ MVP</span>}</div>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  {f.played && <span className="text-emerald-700 font-bold">Played</span>}
                  {f.goals > 0 && <span className="px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-bold">⚽ {f.goals}</span>}
                  {f.assists > 0 && <span className="px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-bold">🎯 {f.assists}</span>}
                  {f.yellow_cards > 0 && <span className="px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-800 font-bold">🟨 {f.yellow_cards}</span>}
                  {f.red_cards > 0 && <span className="px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 font-bold">🟥 {f.red_cards}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function StatBox({ emoji, v, lbl, testid }) {
  return (
    <div className="bg-[#FAFBFD] border border-black/5 rounded-xl p-3 text-center" data-testid={testid}>
      <div className="text-lg">{emoji}</div>
      <div className="font-display font-extrabold text-2xl text-black mt-0.5">{v}</div>
      <div className="text-[10px] text-black/55 uppercase font-bold tracking-wider">{lbl}</div>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-black/5 last:border-0">
      <span className="text-xs uppercase font-bold text-black/55">{label}</span>
      <span className="text-sm text-black/85">{value || "—"}</span>
    </div>
  );
}

function Payments() {
  const [items, setItems] = useState([]);
  useEffect(() => { capi().get("/club/payments").then((r) => setItems(r.data || [])).catch(() => {}); }, []);
  return (
    <div className="p-5 lg:p-8" data-testid="club-payments">
      <h1 className="font-display font-extrabold text-2xl text-black mb-5">My payments</h1>
      <div className="bg-white border border-black/8 rounded-2xl overflow-hidden">
        {items.length === 0 ? <Empty msg="No payments recorded" /> : (
          <div className="divide-y divide-black/5">
            {items.map((p) => (
              <div key={p.id} className="px-5 py-3 flex items-center justify-between" data-testid={`club-payment-${p.id}`}>
                <div>
                  <div className="font-bold text-sm capitalize">{(p.kind || "").replace("_", " ")}</div>
                  <div className="text-xs text-black/55">{(p.submitted_at || p.paid_at || p.created_at || "").slice(0, 16).replace("T", " ")} · {(p.method || "").replace("_", " ")}</div>
                  {p.reference && <div className="text-[10px] text-black/45 mt-0.5">Ref: <code className="font-mono">{p.reference}</code></div>}
                  {p.status === "rejected" && p.rejection_reason && <div className="text-[10px] text-rose-600 mt-0.5">Reason: {p.rejection_reason}</div>}
                </div>
                <div className="text-right">
                  <div className={`font-display font-extrabold text-base ${p.status === "rejected" ? "text-rose-600" : "text-emerald-700"}`}>{ngn(p.amount)}</div>
                  <PaymentStatusBadge status={p.status} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Dues() {
  const [kinds, setKinds] = useState(null);
  const [settings, setSettings] = useState(null);
  const [openKind, setOpenKind] = useState(null);  // which kind detail sheet is open
  const loadDuesIndex = async () => {
    try {
      const r = await capi().get("/club/dues");
      const all = r.data || [];
      // Group existing dues by kind for the index card view
      const grouped = {};
      for (const d of all) {
        const k = d.kind || "other";
        if (!grouped[k]) grouped[k] = { kind: k, pending_count: 0, pending_total: 0, paid_count: 0, last_due_date: "" };
        if (d.status === "pending") {
          grouped[k].pending_count += 1;
          grouped[k].pending_total += Number(d.amount || 0);
        } else if (d.status === "paid") {
          grouped[k].paid_count += 1;
        }
        if ((d.due_date || "") > grouped[k].last_due_date) grouped[k].last_due_date = d.due_date || "";
      }
      // Always surface monthly_dues even if zero existing — members may want to pre-pay future months
      if (!grouped.monthly_dues) grouped.monthly_dues = { kind: "monthly_dues", pending_count: 0, pending_total: 0, paid_count: 0, last_due_date: "" };
      setKinds(Object.values(grouped));
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  useEffect(() => {
    loadDuesIndex();
    capi().get("/club/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);
  if (!kinds) return <div className="p-12 flex items-center justify-center"><Loader2 className="animate-spin text-emerald-600" /></div>;
  return (
    <div className="p-5 lg:p-8" data-testid="club-dues">
      <h1 className="font-display font-extrabold text-2xl text-black mb-1">My dues</h1>
      <p className="text-sm text-black/55 mb-5">Tap a fee to view every period and pay one or more at once.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {kinds.map((k) => (
          <button key={k.kind} onClick={() => setOpenKind(k.kind)} data-testid={`club-due-kind-${k.kind}`}
            className="bg-white border border-black/8 rounded-2xl p-5 text-left hover:border-emerald-400 hover:shadow-md transition-all group">
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase font-bold tracking-wider text-emerald-700">{k.kind.replace("_", " ")}</div>
              <ArrowRight size={14} className="text-black/30 group-hover:text-emerald-600 group-hover:translate-x-0.5 transition-all" />
            </div>
            <div className="mt-2 font-display font-extrabold text-lg text-black capitalize">{k.kind.replace("_", " ")}</div>
            <div className="mt-3 grid grid-cols-2 gap-3">
              <div>
                <div className="text-[10px] uppercase font-bold text-black/55">Pending</div>
                <div className={`font-display font-extrabold text-2xl ${k.pending_count > 0 ? "text-rose-600" : "text-black/30"}`}>{ngn(k.pending_total)}</div>
                <div className="text-[10px] text-black/55">{k.pending_count} period{k.pending_count === 1 ? "" : "s"}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase font-bold text-black/55">Paid</div>
                <div className="font-display font-extrabold text-2xl text-emerald-700">{k.paid_count}</div>
                <div className="text-[10px] text-black/55">period{k.paid_count === 1 ? "" : "s"}</div>
              </div>
            </div>
          </button>
        ))}
      </div>
      {openKind && (
        <KindPeriodsSheet kind={openKind} settings={settings}
          onClose={() => setOpenKind(null)}
          onPaidSubmitted={() => { setOpenKind(null); loadDuesIndex(); toast.success("Payment submitted — awaiting club admin approval"); }} />
      )}
    </div>
  );
}

function KindPeriodsSheet({ kind, settings, onClose, onPaidSubmitted }) {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [step, setStep] = useState("pick"); // pick | bank | confirm
  const [f, setF] = useState({ reference: "", notes: "", method: "bank_transfer" });
  const [busy, setBusy] = useState(false);
  const load = () => capi().get(`/club/dues/periods?kind=${kind}&future=6`).then((r) => setData(r.data)).catch((e) => toast.error(e?.response?.data?.detail || "Failed"));
  useEffect(() => { load(); }, [kind]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!data) return (
    <SheetFrame onClose={onClose}>
      <div className="p-12 flex items-center justify-center"><Loader2 className="animate-spin text-emerald-600" /></div>
    </SheetFrame>
  );
  const periods = data.periods || [];
  const toggle = (p) => {
    if (p.status !== "pending") return;
    const next = new Set(selected);
    if (next.has(p.id)) next.delete(p.id);
    else next.add(p.id);
    setSelected(next);
  };
  const selectedPeriods = periods.filter((p) => selected.has(p.id));
  const totalAmount = selectedPeriods.reduce((s, p) => s + Number(p.amount || 0), 0);
  const hasBank = !!(settings?.bank_account_number);
  const submit = async () => {
    setBusy(true);
    try {
      await capi().post("/club/payments/claim", {
        due_ids: Array.from(selected), kind, method: f.method,
        reference: f.reference, notes: f.notes,
      });
      onPaidSubmitted();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <SheetFrame onClose={onClose}>
      <div className="p-5 lg:p-6 max-h-[88vh] overflow-y-auto" data-testid={`club-periods-sheet-${kind}`}>
        {step === "pick" && (
          <>
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-[10px] uppercase font-bold tracking-wider text-emerald-700">{kind.replace("_", " ")}</div>
                <div className="font-display font-extrabold text-xl text-black capitalize">{kind.replace("_", " ")}</div>
                <div className="text-xs text-black/55 mt-0.5">
                  {data.periodic
                    ? `Billed ${data.frequency} · ${ngn(data.amount_per_period)} per period`
                    : `${periods.length} entr${periods.length === 1 ? "y" : "ies"}`}
                </div>
              </div>
              <button onClick={onClose} className="p-1.5 rounded hover:bg-black/5"><X size={18} /></button>
            </div>
            {periods.length === 0 ? (
              <Empty msg="No dues yet. Periods will appear once your club has billed you." />
            ) : (
              <div className="space-y-1.5">
                {periods.map((p) => {
                  const sel = selected.has(p.id);
                  const isPaid = p.status === "paid";
                  return (
                    <button key={p.id} onClick={() => toggle(p)} disabled={isPaid}
                      data-testid={`club-period-${p.id}`}
                      className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl border text-left transition-all ${
                        isPaid ? "bg-emerald-50/50 border-emerald-200 cursor-default" :
                        sel ? "bg-emerald-50 border-emerald-500 ring-2 ring-emerald-200" :
                        "bg-white border-black/10 hover:border-emerald-300"
                      }`}>
                      <div className={`w-5 h-5 rounded flex items-center justify-center flex-shrink-0 ${
                        isPaid ? "bg-emerald-500 text-white" :
                        sel ? "bg-emerald-600 text-white" :
                        "border-2 border-black/20"
                      }`}>
                        {isPaid && <CheckIcon />}
                        {sel && !isPaid && <CheckIcon />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-sm">{p.period_label || p.title}</div>
                        <div className="text-[10px] text-black/55">
                          {(p.due_date || "").slice(0, 10)}
                          {p.is_future && <span className="ml-1.5 px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-bold uppercase tracking-wider">Future</span>}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`font-display font-extrabold ${isPaid ? "text-emerald-700" : "text-black"}`}>{ngn(p.amount)}</div>
                        <span className={`text-[9px] font-bold uppercase tracking-wider ${isPaid ? "text-emerald-700" : "text-rose-600"}`}>{p.status}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
            <div className="sticky bottom-0 -mx-5 lg:-mx-6 mt-5 px-5 lg:px-6 py-4 border-t border-black/8 bg-white">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="text-[10px] uppercase font-bold text-black/55">Selected · {selected.size}</div>
                  <div className="font-display font-extrabold text-2xl text-emerald-700">{ngn(totalAmount)}</div>
                </div>
                <button onClick={() => setStep("bank")} disabled={selected.size === 0} data-testid="club-period-next"
                  className="px-5 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm disabled:opacity-40">
                  Pay {selected.size} period{selected.size === 1 ? "" : "s"}
                </button>
              </div>
            </div>
          </>
        )}

        {step === "bank" && (
          <>
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase font-bold tracking-wider text-emerald-700">Step 1 of 2 · Send the money</div>
                <div className="font-display font-extrabold text-xl text-black">Pay {ngn(totalAmount)}</div>
                <div className="text-xs text-black/55 mt-0.5">{selectedPeriods.length} period{selectedPeriods.length === 1 ? "" : "s"} selected</div>
              </div>
              <button onClick={onClose} className="p-1.5 rounded hover:bg-black/5"><X size={18} /></button>
            </div>
            {!hasBank ? (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
                Your club administrator has not set bank details yet. Please contact them to arrange payment.
              </div>
            ) : (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm space-y-2">
                <div className="text-[10px] uppercase font-bold tracking-wider text-emerald-800 mb-1">Send {ngn(totalAmount)} to</div>
                <Row k="Bank" v={settings.bank_name || "—"} />
                <Row k="Account #" v={settings.bank_account_number || "—"} copy />
                <Row k="Account name" v={settings.bank_account_holder || "—"} />
                {settings.payment_instructions && (
                  <div className="text-xs text-black/65 pt-2 border-t border-emerald-200">{settings.payment_instructions}</div>
                )}
              </div>
            )}
            <div className="mt-4 bg-white border border-black/10 rounded-xl p-3 text-xs">
              <div className="font-bold mb-1.5">You're paying for:</div>
              <ul className="space-y-0.5 text-black/65">
                {selectedPeriods.map((p) => (
                  <li key={p.id} className="flex justify-between">
                    <span>{p.period_label || p.title}</span>
                    <span className="font-mono">{ngn(p.amount)}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={() => setStep("pick")} className="flex-1 py-3 rounded-xl bg-black/5 text-sm font-bold text-black/70">Back</button>
              <button onClick={() => setStep("confirm")} disabled={!hasBank} data-testid="club-period-pay-next"
                className="flex-1 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm disabled:opacity-50">
                I have sent the money
              </button>
            </div>
          </>
        )}

        {step === "confirm" && (
          <>
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase font-bold tracking-wider text-emerald-700">Step 2 of 2 · Confirm</div>
                <div className="font-display font-extrabold text-xl text-black">Confirm transfer</div>
                <div className="text-xs text-black/55 mt-0.5">Help your admin verify your payment quickly.</div>
              </div>
              <button onClick={onClose} className="p-1.5 rounded hover:bg-black/5"><X size={18} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-[11px] uppercase font-bold text-black/55">Payment method</label>
                <select value={f.method} onChange={(e) => setF({ ...f, method: e.target.value })}
                  className="w-full mt-1 border border-black/15 rounded-lg px-3 py-2.5 text-sm">
                  <option value="bank_transfer">Bank transfer</option>
                  <option value="cash">Cash (handed to executive)</option>
                  <option value="online">Online / other</option>
                </select>
              </div>
              <div>
                <label className="text-[11px] uppercase font-bold text-black/55">Transfer reference / receipt #</label>
                <input value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} data-testid="club-period-ref"
                  placeholder="e.g. ZA-9931" className="w-full mt-1 border border-black/15 rounded-lg px-3 py-2.5 text-sm" />
              </div>
              <div>
                <label className="text-[11px] uppercase font-bold text-black/55">Note (optional)</label>
                <textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} rows={2}
                  className="w-full mt-1 border border-black/15 rounded-lg px-3 py-2.5 text-sm" />
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={() => setStep("bank")} className="flex-1 py-3 rounded-xl bg-black/5 text-sm font-bold text-black/70">Back</button>
              <button onClick={submit} disabled={busy} data-testid="club-period-submit"
                className="flex-1 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm disabled:opacity-50">
                {busy ? "Submitting…" : `Submit ${ngn(totalAmount)} for approval`}
              </button>
            </div>
          </>
        )}
      </div>
    </SheetFrame>
  );
}

function SheetFrame({ children, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end lg:items-center justify-center p-0 lg:p-4" onClick={onClose}>
      <div className="bg-white rounded-t-3xl lg:rounded-2xl max-w-2xl w-full" onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

function CheckIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function Row({ k, v, copy }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-[10px] uppercase font-bold tracking-wider text-black/55 w-24">{k}</span>
      <span className="flex-1 text-sm font-mono break-all">{v}</span>
      {copy && (
        <button onClick={() => { navigator.clipboard.writeText(v); toast.success("Copied"); }}
          className="text-[10px] font-bold text-emerald-700 hover:bg-emerald-100 px-2 py-1 rounded">Copy</button>
      )}
    </div>
  );
}

function ComingSoon({ title, subtitle }) {
  return (
    <div className="p-8 lg:p-12 flex items-center justify-center min-h-[60vh]">
      <div className="text-center max-w-md">
        <div className="w-16 h-16 rounded-2xl bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto mb-4"><Trophy size={28} /></div>
        <h1 className="font-display font-extrabold text-2xl text-black">{title}</h1>
        <p className="text-sm text-black/55 mt-2">{subtitle}</p>
      </div>
    </div>
  );
}

function Card({ title, value, subtitle, accent }) {
  return (
    <div className={`rounded-2xl p-4 bg-gradient-to-br ${accent} text-white`}>
      <div className="text-[10px] uppercase font-bold tracking-wider opacity-80">{title}</div>
      <div className="font-display font-extrabold text-2xl mt-1 capitalize">{value}</div>
      <div className="text-xs opacity-80 mt-1">{subtitle}</div>
    </div>
  );
}
function Empty({ msg }) { return <div className="p-10 text-center text-sm text-black/45">{msg}</div>; }

function MemberLoans() {
  const [items, setItems] = useState([]);
  const [show, setShow] = useState(false);
  const load = () => capi().get("/club/loans").then((r) => setItems(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  return (
    <div className="p-5 lg:p-8" data-testid="club-loans">
      <div className="flex items-center justify-between mb-5">
        <h1 className="font-display font-extrabold text-2xl text-black">Loan Requests</h1>
        <button onClick={() => setShow(true)} data-testid="club-loan-new" className="px-4 py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-bold">Request loan</button>
      </div>
      <div className="space-y-2">
        {items.length === 0 ? <Empty msg="No loan history" /> : items.map((l) => (
          <div key={l.id} className="bg-white border border-black/8 rounded-2xl p-4" data-testid={`club-loan-${l.id}`}>
            <div className="flex items-center justify-between mb-2">
              <div><div className="font-bold">{l.purpose}</div><div className="text-xs text-black/55">{l.term_months} months · {(l.created_at || "").slice(0, 10)}</div></div>
              <div className="text-right"><div className="font-display font-extrabold text-lg">{ngn(l.approved_amount || l.amount)}</div><span className={`text-[10px] font-bold uppercase ${l.status === "rejected" ? "text-red-600" : l.status === "repaid" ? "text-emerald-700" : "text-blue-700"}`}>{l.status}</span></div>
            </div>
            {l.status === "disbursed" && l.repayment_schedule?.length > 0 && (
              <div className="mt-3 border-t border-black/5 pt-3">
                <div className="text-[10px] uppercase font-bold text-black/55 mb-2">Repayment schedule · Balance: <span className="text-emerald-700">{ngn(l.balance)}</span></div>
                <div className="grid grid-cols-3 gap-1.5 text-xs">
                  {l.repayment_schedule.slice(0, 12).map((s) => (
                    <div key={s.id} className={`p-2 rounded ${s.status === "paid" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>#{s.installment_no} · {(s.due_date || "").slice(5, 10)}<br /><strong>{ngn(s.amount)}</strong></div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      {show && <LoanRequestModal onClose={() => setShow(false)} onSaved={() => { setShow(false); load(); }} />}
    </div>
  );
}
function LoanRequestModal({ onClose, onSaved }) {
  const [f, setF] = useState({ amount: 0, term_months: 6, purpose: "", interest_pct: 0 });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.amount || !f.purpose) return toast.error("Amount & purpose required");
    setBusy(true);
    try { await capi().post("/club/loans", { ...f, amount: parseFloat(f.amount), term_months: parseInt(f.term_months) || 1 }); toast.success("Loan requested — pending admin review"); onSaved(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end lg:items-center justify-center p-0 lg:p-4" onClick={onClose}>
      <div className="bg-white rounded-t-3xl lg:rounded-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="club-loan-modal">
        <div className="flex items-center justify-between mb-4"><div className="font-display font-extrabold text-xl">Request a loan</div><button onClick={onClose}><X size={16} /></button></div>
        <div className="space-y-3">
          <div><label className="text-[11px] uppercase font-bold text-black/55">Amount (₦)</label><input type="number" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="club-loan-amount" className="w-full mt-1 border border-black/15 rounded-lg px-3 py-2.5 text-sm" /></div>
          <div><label className="text-[11px] uppercase font-bold text-black/55">Term (months)</label><input type="number" value={f.term_months} onChange={(e) => setF({ ...f, term_months: e.target.value })} className="w-full mt-1 border border-black/15 rounded-lg px-3 py-2.5 text-sm" /></div>
          <div><label className="text-[11px] uppercase font-bold text-black/55">Purpose</label><textarea value={f.purpose} onChange={(e) => setF({ ...f, purpose: e.target.value })} rows={3} data-testid="club-loan-purpose" className="w-full mt-1 border border-black/15 rounded-lg px-3 py-2.5 text-sm" /></div>
        </div>
        <button onClick={submit} disabled={busy} data-testid="club-loan-submit" className="w-full mt-5 py-3 rounded-xl bg-emerald-600 text-white font-bold text-sm disabled:opacity-50">{busy ? "Submitting…" : "Submit request"}</button>
      </div>
    </div>
  );
}

function MemberDraft() {
  const [d, setD] = useState(null);
  useEffect(() => { capi().get("/club/draft").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <div className="p-12 flex items-center justify-center"><Loader2 className="animate-spin text-emerald-600" /></div>;
  return (
    <div className="p-5 lg:p-8" data-testid="club-draft">
      <h1 className="font-display font-extrabold text-2xl text-black mb-5">Draft Competition</h1>
      {d.my_team ? (
        <div className="rounded-2xl p-5 text-white mb-5" style={{ background: d.my_team.color || "#1FA868" }}>
          <div className="text-xs uppercase font-bold tracking-wider opacity-80">My team</div>
          <h2 className="font-display font-extrabold text-3xl mt-1">{d.my_team.name}</h2>
          {d.my_player_card && (
            <div className="grid grid-cols-4 gap-2 mt-4">
              <Mini lbl="Goals" v={d.my_player_card.goals || 0} />
              <Mini lbl="Assists" v={d.my_player_card.assists || 0} />
              <Mini lbl="Matches" v={d.my_player_card.matches_played || 0} />
              <Mini lbl="Cards" v={(d.my_player_card.yellow_cards || 0) + (d.my_player_card.red_cards || 0)} />
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white border border-black/8 rounded-2xl p-5 mb-5 text-center text-sm text-black/55">You haven't been assigned to a team yet.</div>
      )}
      <div className="bg-white border border-black/8 rounded-2xl overflow-hidden">
        <div className="px-5 py-3 border-b border-black/5 font-display font-extrabold text-base">League table</div>
        {d.league_table.length === 0 ? <Empty msg="No teams yet" /> : (
          <table className="w-full text-sm">
            <thead className="bg-black/[0.02] text-[11px] uppercase tracking-wide font-bold text-black/55"><tr><th className="text-left px-4 py-2">#</th><th className="text-left px-4 py-2">Team</th><th className="text-right px-4 py-2">P</th><th className="text-right px-4 py-2">W</th><th className="text-right px-4 py-2">D</th><th className="text-right px-4 py-2">L</th><th className="text-right px-4 py-2">GD</th><th className="text-right px-4 py-2 font-extrabold">PTS</th></tr></thead>
            <tbody>{d.league_table.map((t, i) => { const s = t.stats || {}; return (
              <tr key={t.id} className="border-t border-black/5">
                <td className="px-4 py-2.5 font-bold text-black/40">{i + 1}</td>
                <td className="px-4 py-2.5 flex items-center gap-2"><div className="w-4 h-4 rounded-sm" style={{ background: t.color || "#1FA868" }} /> <span className="font-bold">{t.name}</span></td>
                <td className="px-4 py-2.5 text-right">{s.P || 0}</td>
                <td className="px-4 py-2.5 text-right">{s.W || 0}</td>
                <td className="px-4 py-2.5 text-right">{s.D || 0}</td>
                <td className="px-4 py-2.5 text-right">{s.L || 0}</td>
                <td className="px-4 py-2.5 text-right">{s.GD || 0}</td>
                <td className="px-4 py-2.5 text-right font-display font-extrabold">{s.PTS || 0}</td>
              </tr>
            ); })}</tbody>
          </table>
        )}
      </div>
    </div>
  );
}
function Mini({ lbl, v }) { return <div className="bg-white/15 backdrop-blur rounded-xl p-2 text-center"><div className="text-[9px] uppercase font-bold opacity-80">{lbl}</div><div className="font-display font-extrabold text-xl">{v}</div></div>; }

function MemberMatches() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    capi()
      .get("/club/fixtures")
      .then((r) => setItems(r.data || []))
      .catch(() => {});
  }, []);

  return (
    <div className="p-4 lg:p-8" data-testid="club-matches">
      <div className="mb-6">
        <h1 className="font-display font-extrabold text-2xl text-black">
          Match Schedule
        </h1>
        <p className="text-sm text-black/50 mt-1">
          Upcoming and completed club fixtures
        </p>
      </div>

      {items.length === 0 ? (
        <Empty msg="No matches scheduled" />
      ) : (
        <div className="space-y-4">
          {items.map((f) => {
            const date = f.scheduled_at
              ? new Date(f.scheduled_at)
              : null;

            return (
              <div
                key={f.id}
                className="bg-white rounded-3xl border border-black/5 shadow-sm overflow-hidden"
              >
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-black/5">
                  <div>
                    <div className="text-xs font-semibold text-slate-500">
                      {date
                        ? date.toLocaleDateString()
                        : "Date TBA"}
                    </div>

                    <div className="text-xs text-slate-400">
                      {date
                        ? date.toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : ""}
                    </div>
                  </div>

                  <span
                    className={`px-3 py-1 rounded-full text-[11px] font-bold capitalize ${
                      f.status === "finished"
                        ? "bg-emerald-100 text-emerald-700"
                        : "bg-blue-100 text-blue-700"
                    }`}
                  >
                    {f.status}
                  </span>
                </div>

                {/* Teams */}
                <div className="p-5">
                  <div className="flex items-center justify-between gap-3">
                    {/* Home Team */}
                    <div className="flex-1 text-center">
                      <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-emerald-100 flex items-center justify-center text-lg">
                        ⚽
                      </div>

                      <h3 className="font-bold text-sm text-slate-900 leading-tight">
                        {f.team_a_name}
                      </h3>
                    </div>

                    {/* Score / VS */}
                    <div className="px-2">
                      {f.status === "finished" ? (
                        <div className="font-display font-extrabold text-xl text-black whitespace-nowrap">
                          {f.team_a_score} - {f.team_b_score}
                        </div>
                      ) : (
                        <div className="font-black text-slate-400 text-lg">
                          VS
                        </div>
                      )}
                    </div>

                    {/* Away Team */}
                    <div className="flex-1 text-center">
                      <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-blue-100 flex items-center justify-center text-lg">
                        ⚽
                      </div>

                      <h3 className="font-bold text-sm text-slate-900 leading-tight">
                        {f.team_b_name}
                      </h3>
                    </div>
                  </div>

                  {/* Venue */}
                  <div className="mt-5 pt-4 border-t border-black/5">
                    <div className="text-center text-xs text-slate-500">
                      📍 {f.venue || "Venue TBA"}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function MemberEvents() {
  const [items, setItems] = useState([]);
  const load = () => capi().get("/club/events").then((r) => setItems(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const rsvp = async (eid, going) => { try { await capi().post(`/club/events/${eid}/rsvp`, { going }); toast.success(going ? "You're going!" : "RSVP updated"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  return (
    <div className="p-5 lg:p-8" data-testid="club-events">
      <h1 className="font-display font-extrabold text-2xl text-black mb-5">Events</h1>
      <div className="space-y-3">
        {items.length === 0 ? <Empty msg="No events" /> : items.map((e) => (
          <div key={e.id} className="bg-white border border-black/8 rounded-2xl p-5" data-testid={`club-event-${e.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div><div className="text-[10px] uppercase font-bold tracking-wider text-emerald-700">{e.kind}</div><h3 className="font-display font-extrabold text-lg text-black mt-0.5">{e.title}</h3><div className="text-xs text-black/55 mt-1">{(e.starts_at || "").slice(0, 16).replace("T", " ")} · {e.venue || "—"}</div></div>
              <div className="flex gap-1.5">
                <button onClick={() => rsvp(e.id, true)} data-testid={`club-event-going-${e.id}`} className={`px-3 py-1.5 rounded-lg text-xs font-bold ${e.my_rsvp?.going === true ? "bg-emerald-600 text-white" : "bg-emerald-50 text-emerald-700"}`}>Going</button>
                <button onClick={() => rsvp(e.id, false)} className={`px-3 py-1.5 rounded-lg text-xs font-bold ${e.my_rsvp?.going === false ? "bg-red-600 text-white" : "bg-red-50 text-red-700"}`}>Can't make it</button>
              </div>
            </div>
            {e.description && <p className="text-sm text-black/65 mt-3">{e.description}</p>}
          </div>
        ))}
      </div>
    </div>
  );
}

function MemberExecs() {
  const [items, setItems] = useState([]);
  useEffect(() => { capi().get("/club/executives").then((r) => setItems(r.data || [])).catch(() => {}); }, []);
  return (
    <div className="p-5 lg:p-8" data-testid="club-execs">
      <h1 className="font-display font-extrabold text-2xl text-black mb-5">Club Executives</h1>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {items.length === 0 ? <div className="col-span-full"><Empty msg="No executives yet" /></div> : items.map((e) => (
          <div key={e.id} className="bg-white border border-black/8 rounded-2xl p-5 flex items-center gap-3" data-testid={`club-exec-${e.id}`}>
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-600 text-white flex items-center justify-center font-display font-extrabold text-2xl overflow-hidden flex-shrink-0">
              {e.photo_url ? <img src={e.photo_url} alt={e.member_name} className="w-full h-full object-cover" /> : (e.member_name || "?")[0]}
            </div>
            <div className="flex-1 min-w-0"><div className="text-[10px] uppercase font-bold tracking-wider text-violet-700">{(e.position || "").replace(/_/g, " ")}</div><div className="font-display font-extrabold text-base truncate">{e.member_name}</div>{e.phone && <div className="text-xs text-black/55">{e.phone}</div>}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function MemberNotifs() {
  const [items, setItems] = useState([]);
  const load = () => capi().get("/club/notifications").then((r) => setItems(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const markRead = async (nid) => { try { await capi().post(`/club/notifications/${nid}/read`, {}); load(); } catch { /* silent */ } };
  return (
    <div className="p-5 lg:p-8" data-testid="club-notifs">
      <h1 className="font-display font-extrabold text-2xl text-black mb-5">Notifications</h1>
      <div className="bg-white border border-black/8 rounded-2xl overflow-hidden">
        {items.length === 0 ? <Empty msg="No notifications" /> : (
          <div className="divide-y divide-black/5">
            {items.map((n) => (
              <button key={n.id} onClick={() => !n.read && markRead(n.id)} data-testid={`club-notif-${n.id}`}
                className={`w-full text-left px-5 py-3 flex items-start gap-3 hover:bg-black/[0.02] ${!n.read ? "bg-emerald-50/30" : ""}`}>
                <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${!n.read ? "bg-emerald-500" : "bg-black/10"}`} />
                <div className="flex-1 min-w-0"><div className="font-bold text-sm">{n.title}</div><div className="text-xs text-black/65 mt-0.5">{n.body}</div><div className="text-[10px] text-black/45 mt-1">{(n.created_at || "").slice(0, 16).replace("T", " ")}</div></div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

